// React Imports
import { useEffect } from 'react'

// Router Imports
import { useNavigate } from 'react-router-dom'

// Hook Imports
import { useRBAC } from '@/hooks/useRBAC'

// Type Imports
import type { Module } from '@/types/rbac'

// Util Imports
import { getDefaultRoute } from '@/utils/rbac'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredModule: Module
  fallback?: React.ReactNode
}

/**
 * Protected Route Component
 * Wraps content that requires specific module access.
 * Redirects straight to the nearest page the user is actually allowed to
 * see, rather than stopping on an interstitial 401 page.
 */
export const ProtectedRoute = ({ children, requiredModule, fallback = null }: ProtectedRouteProps) => {
  const navigate = useNavigate()
  const { hasModuleAccess, isAuthenticated, isLoading, permissions, userRole } = useRBAC()

  useEffect(() => {
    if (isLoading) return

    if (!isAuthenticated) {
      navigate('/en/login')

      return
    }

    if (!hasModuleAccess(requiredModule)) {
      navigate(getDefaultRoute(permissions, userRole), { replace: true })
    }
  }, [isAuthenticated, isLoading, hasModuleAccess, requiredModule, permissions, userRole, navigate])

  if (isLoading) {
    return fallback || <div>Loading...</div>
  }

  if (!isAuthenticated || !hasModuleAccess(requiredModule)) {
    return fallback
  }

  return <>{children}</>
}
